// React Hook for SignalR Communication Hub

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { communicationHub } from '../services/signalr/communicationHub';
import { useAuthStore } from '../store/authstore';

/**
 * Hook to manage SignalR connection lifecycle
 * Connects when user is authenticated, disconnects on unmount
 */
export const useCommunicationHub = () => {
  const { session } = useAuthStore();
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userId = session?.user?.id;

  const isConnected = useSyncExternalStore(
    (onChange) => communicationHub.onConnectionStateChange(onChange),
    () => communicationHub.isConnected()
  );

  useEffect(() => {
    if (!userId) return;

    // Cancel any pending stop from a StrictMode fake-unmount
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }

    // Read the token lazily so a proactive token refresh doesn't require
    // tearing down and reopening the connection (see communicationHub.start).
    communicationHub.start(() => useAuthStore.getState().session?.accessToken ?? '').catch((error) => {
      console.error('[useCommunicationHub] Connection failed:', error);
    });

    return () => {
      // Delay stop by one tick — StrictMode remounts synchronously so the
      // next start() will cancel this before it fires. On real unmount
      // (logout/navigation) no remount follows, so stop() runs normally.
      stopTimerRef.current = setTimeout(() => {
        stopTimerRef.current = null;
        communicationHub.stop();
      }, 0);
    };
  }, [userId]);

  return {
    isConnected,
  };
};

/**
 * Hook to subscribe to a SignalR event
 * Automatically handles subscribe/unsubscribe
 */
export const useSignalREvent = <T = any>(
  eventName: string,
  handler: (data: T) => void
) => {
  const handlerRef = useRef(handler);

  // Update ref when handler changes (avoid stale closures)
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const wrappedHandler = (data: T) => {
      console.debug(`[SignalR] Event received: ${eventName}`, data);
      handlerRef.current(data);
    };

    // Subscribe to event
    communicationHub.on(eventName, wrappedHandler);

    console.log(`[useSignalREvent] Subscribed to: ${eventName}`);

    // Unsubscribe on unmount
    return () => {
      communicationHub.off(eventName, wrappedHandler);
      console.log(`[useSignalREvent] Unsubscribed from: ${eventName}`);
    };
  }, [eventName]);
};
